import React from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'
import Plans from './Plans'
import { sendAnalyticsEvents } from '../../analytics/common'
import { EVENTS } from '../../analytics/events'
import { useDispatch, useSelector } from 'react-redux'
import { selectEventsData, setEventDataList } from '../events/eventsSlice'
import { selectLandingType, selectPlan, selectShowPaywall, setShowCheckout } from './plansSlice'
import { selectName } from '../family/familySlice'
import { fontSize, wh, tabletMinWidth } from '../../mixins'
import MainContainer from '../../UI/MainContainer'
import Timer from '../../UI/Timer'
import { InvertedLogo } from '../../UI/Logo'
import UIButton from '../../UI/UIButton'
import { Forbes, TechCrunch, Mashable } from '../family/logos'
import { PLANS } from '../../analytics/eCommerce'
import useWindowWidth from '../../helper/useWindowWidth'
import CrossBtn from './CrossBtn'
import { selectStripeAccountName } from '../signup/signupSlice'

const isArabic = navigator.language.startsWith('ar')
const textAlign = isArabic ? 'right' : 'left'

const StyledBg = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  background: linear-gradient(180deg, #005EB5 0%, #001F3D 100%);
  opacity: 0.7;
  z-index: 2000;
`;

const StyledContainer = styled.div`
  width: 100%;
  height: ${({showPaywall}) => showPaywall ? '100%' : 'unset'};
  position: absolute;
  z-index: 2100;
  background-color: #ffffff;
  ${({showPaywall}) => showPaywall && css`
    overflow-y: scroll;
    overflow-x: hidden;
    max-width: 470px;

    &::-webkit-scrollbar {
       display: none;
    }
  `}

  @media screen and (max-width: 450px) {
    max-width: 100vw;
  }
`;

const Title = styled.h1`
  font-weight: 600;
  ${fontSize(32)};
  line-height: 44px;
  color: #323232;
  text-align: ${textAlign};
  margin: 0 0 12px 0;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;

  @media screen and (min-width: 745px) {
    margin: ${({showPaywall}) => !showPaywall && '102px 0 12px 0'};
  }
`
const CrossBtnContainer = styled.div`
  max-width: 330px;
  width: 100%;
  z-index: 3000;
  position: relative;
  margin: 0 auto;
`

const Description = styled.p`
  font-weight: 400;
  ${fontSize(16)};
  line-height: 22px;
  color: #323232;
  text-align: ${textAlign};
  margin: 0 0 24px 0;

  > span {
    color: #3393ec;
  }
`

const PlansWrapper = styled.div`
  margin: 0 12px;
  ${({ paddingTop }) => paddingTop && `padding-top: ${paddingTop}px`};
  position: relative;
  max-width: 330px;
`

const TrialHint = styled.span`
  font-weight: 400;
  ${fontSize(12)};
  line-height: 16px;
  color: #818181;
  margin: 8px 0 4px;
  z-index: 1;
  transition: opacity 0.3s;
  opacity: ${({ show }) => (show ? 1 : 0)};

  @media screen and (min-width: ${tabletMinWidth}px) {
    margin-bottom: 18px;
  }
`

const SecondaryTitle = styled.h1`
  font-weight: 600;
  ${fontSize(20)};
  line-height: 27px;
  color: #323232;
  text-align: center;
  margin: 20px 0 16px 0;

  @media screen and (min-width: ${tabletMinWidth}px) {
    margin-bottom: 6px;
  }
`

const StyledList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 24px 0;
`

const StyledItem = styled.li`
    position: relative;
    padding-${textAlign}: 20px;
    font-weight: 400;
    ${fontSize(14)};
    line-height: 20px;
    color: #323232;
    text-align: ${textAlign};
  
    svg {
        ${wh('12px')};
        position: absolute;
        top: 4px;
        ${textAlign}: 0;
    }
  
    &:not(:last-child) {
        margin-bottom: 8px;
    }
`

const PressLogos = styled.div`
  display: flex;
  flex-direction: revert;
  justify-content: center;
  flex-wrap: wrap;

  svg {
    width: 40%;
    &:not(:last-child) {
      margin-bottom: 26px;
    }
  }
`

const ReviewItem = styled.div`
  background: #f4f4f4;
  border-radius: 13px;
  padding: 12px 16px;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 24px;
  }
`

const ReviewHead = styled.div`
  display: flex;
  flex-direction: row${isArabic ? '-reverse' : ''};
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 8px;
`

const ReviewBody = styled.div`
  display: flex;
  flex-direction: row${isArabic ? '-reverse' : ''};
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;
`

const ReviewerPhoto = styled.img`
  width: 30px;
  height: 30px;
`

const ReviewerName = styled.span`
  font-weight: 690;
  font-size: 14px;
  line-height: 20px;
  color: #323232;
`

const UserIcon = styled.div`
  ${wh('14px')};
  background-image: url('../assets/app-icon.png');
  background-size: 100%;
  background-repeat: no-repeat;
  background-position: center;
`

const Label = styled.div`
  background: #e9e9e9;
  border-radius: 2px;
  padding: 2px;
  margin: 0 7px;
  font-weight: 600;
  ${fontSize(6)};
  line-height: 7px;
  letter-spacing: -0.24px;
  text-transform: capitalize;
  color: #5b5b5b;
`

const Rate = styled.div`
  ${wh('53px', '10px')};
  background-image: url('../assets/stars.png');
  background-size: 100%;
  background-repeat: no-repeat;
  background-position: center;
`

const ReviewText = styled.div`
  font-weight: 400;
  ${fontSize(14)};
  line-height: 17px;
  letter-spacing: -0.24px;
  text-align: ${textAlign};
`

const LogoWrapper = styled.div`
  position: absolute;
  left: 64px;
  top: 35px;
  ${({showPaywall}) => showPaywall && `
    max-width: 330px;
    margin: 0 auto;
    position: relative;
    top: 0px;
    left: 0px;
    padding: 20px 0px;
    > img {
      margin-left: -10px;
    }
  `}
  @media screen and (max-width: ${tabletMinWidth}px) {
    position: static;
    margin-left: -6px;
    width: 100%;
    padding: 20px 0;
  }
`

const DefaultPlans = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const plan = useSelector(selectPlan)
  const eventsData = useSelector(selectEventsData)
  const name = useSelector(selectName)
  const windowWidth = useWindowWidth()
  const landingType = useSelector(selectLandingType)
  const showPaywall = useSelector(selectShowPaywall);
  const stripeAccountName = useSelector(selectStripeAccountName);

  const FEATURES = [
    t('know_when_family_come_or_go'),
    t('realtime_location_sharing'),
    t('find_loved_ones'),
    t('be_notified_in_case'),
    t('get_notified_when_the_land'),
  ]
  const renderItem = (i, index) => {
    return (
      <StyledItem key={index}>
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M5.53828 13C5.83759 13 6.07425 12.8666 6.2413 12.6068L12.819 2.15857C12.9443 1.95494 13 1.80047 13 1.63897C13 1.25278 12.7425 1 12.3596 1C12.0882 1 11.9281 1.09128 11.761 1.3581L5.51044 11.4061L2.26682 7.12288C2.09281 6.87712 1.92575 6.77882 1.66821 6.77882C1.27842 6.77882 1 7.05266 1 7.43885C1 7.60035 1.06961 7.78291 1.20186 7.95143L4.81439 12.5927C5.03016 12.8666 5.23898 13 5.53828 13Z"
            fill="#3393EC"
            stroke="#3393EC"
          />
        </svg>
        {i}
      </StyledItem>
    )
  }
  const REVIEWS = [
    {
      market: 'IOS',
      reviewer: 'Kevin Peters',
      reviewerSource: '../assets/reviewer1.png',
      text: t('review_1'),
    },
    {
      market: 'Play',
      reviewer: 'John Lee',
      reviewerSource: '../assets/reviewer2.png',
      text: t('review_2'),
    },
    {
      market: 'Play',
      reviewer: 'Megan Smith',
      reviewerSource: '../assets/reviewer3.png',
      text: t('review_3'),
    },
  ]
  const renderReview = (r, i) => {
    return (
      <ReviewItem key={i}>
        <ReviewHead>
          <UserIcon />
          <Label>{r.market}</Label>
          <Rate />
        </ReviewHead>
        <ReviewBody>
          <ReviewerPhoto src={r.reviewerSource} alt={r.reviewer}/>
          <ReviewerName>{r.reviewer}</ReviewerName>
        </ReviewBody>
        <ReviewText>{r.text}</ReviewText>
      </ReviewItem>
    )
  }

  const onContinueClick = () => {
    const { productId, duration, description, periodPrice, price } = PLANS[plan]
    const planData = {
      Value: price,
      value: Math.round(periodPrice * 100),
      currency: 'USD',
      price: periodPrice,
      Duration: duration,
      duration,
      description,
      periodPrice,
      product_id: productId,
      stripe_account_name: stripeAccountName,
    }
    dispatch(setEventDataList(planData))
    sendAnalyticsEvents(showPaywall ? EVENTS.mapPaywallChosen : EVENTS.onboardingPaywallChosen, {
      ...eventsData,
      ...planData,
    })
    dispatch(setShowCheckout(true))
  }

  const continueButton = (displayStickyBtn, sticky) => (
    <UIButton onClick={onContinueClick} bottomPosition={'24px'} displayStickyBtn={displayStickyBtn} position={sticky}>
      {(landingType === 'main' || landingType === 'map' || landingType === 'discountb') ? t('start_tracking') : t('continue')}
    </UIButton>
  )
  const trialHint = (
    <TrialHint show={plan === 'fl-1-month-intro-franko' || plan === 'fl-monthly-free3d-2999'}>
      {t((plan === 'fl-1-month-intro-franko') ? 'trial_hint_7' : 'trial_hint_3')}
    </TrialHint>
  )

  return (
    <>
      {showPaywall && <StyledBg />}
      <StyledContainer showPaywall={showPaywall}>
        {(windowWidth > tabletMinWidth) && (
          <LogoWrapper showPaywall={showPaywall}>
            <InvertedLogo width={62} height={62} />
            {(landingType === 'map' && showPaywall) && <CrossBtn />}
          </LogoWrapper>
        )}
        <MainContainer customPadding={'0 5px'} customStyles={'height: unset'} customDesktopStyles={'height: unset'}>
          <CrossBtnContainer>{((windowWidth > tabletMinWidth) && landingType === 'map' && !showPaywall) && <CrossBtn/>}</CrossBtnContainer>
          <PlansWrapper>
            {(windowWidth <= tabletMinWidth) && (
              <LogoWrapper showPaywall={showPaywall}>
                <InvertedLogo width={62} height={62} />
              </LogoWrapper>
            )}
            {(landingType === 'map' && (windowWidth <= tabletMinWidth)) && <CrossBtn/>}
            <Title showPaywall={showPaywall}>{`${t('hey')} ${decodeURIComponent(name)}`}</Title>
            <Description>
              {t('based_on_your_answers')} <span>{t('this_is_your_family')}</span>
            </Description>
            <Timer />
            <Plans />
            <SecondaryTitle>{t('what_you_get')}:</SecondaryTitle>
            <StyledList>{FEATURES.map(renderItem)}</StyledList>
          </PlansWrapper>
          {continueButton(true, 'sticky')}
          <PlansWrapper>
            <SecondaryTitle>{t('as_seen_on')}</SecondaryTitle>
            <PressLogos>
              {Forbes}
              {TechCrunch}
              {Mashable}
            </PressLogos>
            <SecondaryTitle>{t('customer_reviews')}</SecondaryTitle>
            {REVIEWS.map(renderReview)}
          </PlansWrapper>
          {continueButton(false)}
          {trialHint}
        </MainContainer>
      </StyledContainer>
    </>
  )
}

export default DefaultPlans
